import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Layers, Check, Zap } from 'lucide-react';
import { NeMoLoRAConfig as LoRAConfigType } from '@/mock/gpu-instance';

interface NeMoLoRAConfigProps {
  config: LoRAConfigType;
  onChange: (config: LoRAConfigType) => void;
  isQLoRA?: boolean;
}

const TARGET_MODULES = [
  { id: 'q_proj', label: 'Q Projection', description: 'Query projection layer' },
  { id: 'k_proj', label: 'K Projection', description: 'Key projection layer' },
  { id: 'v_proj', label: 'V Projection', description: 'Value projection layer' },
  { id: 'o_proj', label: 'O Projection', description: 'Output projection layer' },
  { id: 'gate_proj', label: 'Gate Projection', description: 'MLP gate layer' },
  { id: 'up_proj', label: 'Up Projection', description: 'MLP up projection' },
  { id: 'down_proj', label: 'Down Projection', description: 'MLP down projection' },
];

const RANK_OPTIONS = [8, 16, 32, 64, 128];

const NeMoLoRAConfig = ({
  config,
  onChange,
  isQLoRA = false,
}: NeMoLoRAConfigProps) => {
  const handleChange = <K extends keyof LoRAConfigType>(
    key: K,
    value: LoRAConfigType[K]
  ) => {
    onChange({ ...config, [key]: value });
  };

  const toggleModule = (moduleId: string) => {
    const newModules = config.targetModules.includes(moduleId)
      ? config.targetModules.filter((m) => m !== moduleId)
      : [...config.targetModules, moduleId];
    handleChange('targetModules', newModules);
  };

  return (
    <div className="space-y-4">
      {/* LoRA Rank */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Layers className="h-4 w-4" />
            LoRA Configuration
          </CardTitle>
          <CardDescription>
            Configure Low-Rank Adaptation parameters
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Rank Selection */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Rank (r)</Label>
              <span className="text-sm text-muted-foreground font-mono">{config.rank}</span>
            </div>
            <Select
              value={config.rank.toString()}
              onValueChange={(value) => handleChange('rank', parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RANK_OPTIONS.map((rank) => (
                  <SelectItem key={rank} value={rank.toString()}>
                    {rank} {rank === 16 && '(Recommended)'} {rank >= 64 && '(High VRAM)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Higher rank = more parameters = better quality but more VRAM
            </p>
          </div>

          {/* Alpha */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Alpha</Label>
              <span className="text-sm text-muted-foreground font-mono">{config.alpha}</span>
            </div>
            <Slider
              value={[config.alpha]}
              onValueChange={([value]) => handleChange('alpha', value)}
              min={1}
              max={128}
              step={1}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>1</span>
              <span>Scaling factor (commonly 2x rank)</span>
              <span>128</span>
            </div>
          </div>

          {/* Dropout */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Dropout</Label>
              <span className="text-sm text-muted-foreground font-mono">{config.dropout}</span>
            </div>
            <Slider
              value={[config.dropout]}
              onValueChange={([value]) => handleChange('dropout', value)}
              min={0}
              max={0.5}
              step={0.01}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0</span>
              <span>Regularization (0.05 recommended)</span>
              <span>0.5</span>
            </div>
          </div>

          {/* QLoRA Quantization */}
          {isQLoRA && (
            <div className="space-y-2 pt-2 border-t">
              <Label>Quantization</Label>
              <Select
                value={config.quantization || '4bit'}
                onValueChange={(value) => handleChange('quantization', value as '4bit' | '8bit')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="4bit">4-bit (NF4) - Recommended</SelectItem>
                  <SelectItem value="8bit">8-bit (INT8)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                4-bit quantization significantly reduces VRAM with minimal quality loss
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Target Modules */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Target Modules
          </CardTitle>
          <CardDescription>
            Select which layers to apply LoRA adapters to
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            {TARGET_MODULES.map((module) => {
              const isSelected = config.targetModules.includes(module.id);
              return (
                <div
                  key={module.id}
                  className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'hover:border-muted-foreground/50'
                  }`}
                  onClick={() => toggleModule(module.id)}
                >
                  <div
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border mt-0.5 ${
                      isSelected
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-muted-foreground/50'
                    }`}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </div>
                  <div>
                    <div className="text-sm font-medium">{module.label}</div>
                    <div className="text-xs text-muted-foreground">{module.description}</div>
                  </div>
                </div>
              );
            })}
          </div>
          {config.targetModules.length > 0 && (
            <div className="mt-3 pt-3 border-t">
              <div className="text-xs text-muted-foreground mb-2">
                Selected ({config.targetModules.length}):
              </div>
              <div className="flex flex-wrap gap-1">
                {config.targetModules.map((mod) => (
                  <Badge key={mod} variant="secondary" className="text-xs">
                    {mod}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      <Card className="bg-muted/30">
        <CardContent className="pt-4">
          <div className="text-sm font-medium mb-2">LoRA Summary</div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="text-muted-foreground">Rank:</div>
            <div className="font-mono">{config.rank}</div>
            <div className="text-muted-foreground">Alpha:</div>
            <div className="font-mono">{config.alpha}</div>
            <div className="text-muted-foreground">Scaling:</div>
            <div className="font-mono">{(config.alpha / config.rank).toFixed(2)}x</div>
            <div className="text-muted-foreground">Dropout:</div>
            <div className="font-mono">{(config.dropout * 100).toFixed(0)}%</div>
            <div className="text-muted-foreground">Target Modules:</div>
            <div className="font-mono">{config.targetModules.length}</div>
            {isQLoRA && (
              <>
                <div className="text-muted-foreground">Quantization:</div>
                <div className="font-mono">{config.quantization || '4bit'}</div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NeMoLoRAConfig;
